import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { authClient } from '@frontend/api-clients/authClient';
import type { User } from './types';
import { PublicIntegrationProvider } from './context/PublicIntegrationContext';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import PublicLandingPage from './pages/public/PublicLandingPage';
import EmailVerificationPage from './pages/auth/EmailVerificationPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import OAuthCallbackPage from './pages/auth/OAuthCallbackPage';
import ServiceLaunchPage from './pages/auth/ServiceLaunchPage';
import ServiceLoginPage from './pages/auth/ServiceLoginPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import EcosystemSettingsPage from './pages/settings/EcosystemSettingsPage';
import EcosystemAdminPage from './pages/admin/EcosystemAdminPage';
import {
  portalLaunchRoutePath,
  resolveServicePath,
} from './config/serviceUrlRegistry';

const PORTAL_OAUTH_CALLBACK_PATH = resolveServicePath('portal', 'callback');
const PORTAL_SERVICE_LOGIN_PATH = resolveServicePath('portal', 'service_login');
const PORTAL_LAUNCH_ROUTE = portalLaunchRoutePath();
const PORTAL_LANDING_PATH = resolveServicePath('portal', 'landing');

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if we just returned from logout
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('logout') === 'complete') {
      // Clear all auth data now that we're back from Authentik end-session
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      localStorage.removeItem('id_token');
      localStorage.removeItem('access_token');
      sessionStorage.clear();
      
      // Clear all cookies
      document.cookie.split(";").forEach((c) => {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });
      
      console.log('✅ Logout complete - all auth data cleared');
      
      // Remove the logout parameter and reload to clean URL
      window.history.replaceState({}, document.title, '/');
    }
    
    const loadUser = async () => {
      const currentUser = await authClient.getCurrentUser();
      setUser(currentUser);
      setLoading(false);
    };
    loadUser();
  }, []);

  if (loading) {
    return (
      <PublicIntegrationProvider>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          color: 'white',
          fontSize: '1.5rem'
        }}>
          Loading...
        </div>
      </PublicIntegrationProvider>
    );
  }

  return (
    <PublicIntegrationProvider>
      <BrowserRouter>
      <Routes>
        <Route path="/" element={<PublicLandingPage />} />
        <Route path="/login" element={
          user ? <Navigate to={PORTAL_LANDING_PATH} /> : <LoginPage />
        } />
        <Route path="/register" element={
          user ? <Navigate to={PORTAL_LANDING_PATH} /> : <RegisterPage />
        } />
        <Route path={PORTAL_OAUTH_CALLBACK_PATH} element={<OAuthCallbackPage />} />
        <Route path={PORTAL_SERVICE_LOGIN_PATH} element={<ServiceLoginPage />} />
        <Route path={PORTAL_LAUNCH_ROUTE} element={<ServiceLaunchPage />} />
        <Route path="/verify-email" element={<EmailVerificationPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path={PORTAL_LANDING_PATH} element={
          user ? <DashboardPage user={user} /> : <Navigate to="/login" replace />
        } />
        <Route path="/settings/ecosystems" element={
          user
            ? <EcosystemSettingsPage user={user} />
            : <Navigate to="/login" />
        } />
        <Route path="/admin" element={
          user && user.groups.includes('patriot-admins')
            ? <AdminDashboardPage user={user} />
            : <Navigate to="/dashboard" />
        } />
        <Route path="/admin/ecosystems" element={
          user && user.groups.includes('patriot-admins')
            ? <EcosystemAdminPage user={user} />
            : <Navigate to="/dashboard" />
        } />
      </Routes>
    </BrowserRouter>
    </PublicIntegrationProvider>
  );
}

export default App;
